<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </button>
      <h1 class="title">待上架</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div v-if="loading" class="loading">加载中...</div>
      <div v-else-if="errorMsg" class="error">{{ errorMsg }}</div>

      <div v-else-if="detail" class="detail">
        <section class="section">
          <div class="section-row">
            <div class="label">入库单号</div>
            <div class="value">{{ detail.wareInNo || '-' }}</div>
          </div>
          <div class="section-row">
            <div class="label">创建时间</div>
            <div class="value">{{ detail.createTime || '-' }}</div>
          </div>
          <div class="section-row">
            <div class="label">供应商</div>
            <div class="value">{{ detail.supplierName || '-' }}</div>
          </div>
          <div class="section-row">
            <div class="label">入库类型</div>
            <div class="value">{{ wareInTypeText(detail.wareInType) }}</div>
          </div>
        </section>

        <div class="section-title">托盘信息</div>

        <section v-for="(item, index) in palletItems" :key="item.id || index" class="section">
          <div class="section-row bold">
            <div class="label">托盘码</div>
            <div class="value">{{ item.palletCode || '-' }}</div>
          </div>
          <div class="divider"></div>
          <div class="section-row">
            <div class="label">物料编码</div>
            <div class="value">{{ item.materialNo || '-' }}</div>
          </div>
          <div class="section-row">
            <div class="label">物料描述</div>
            <div class="value">{{ item.materialDesc || '-' }}</div>
          </div>
          <div class="section-row">
            <div class="label">数量</div>
            <div class="value">{{ item.qtyText }}</div>
          </div>
          <div class="section-row">
            <div class="label">批次号</div>
            <div class="value">{{ item.batchNo || '-' }}</div>
          </div>

          <div class="action-row">
            <button class="primary-btn" @click="handlePutaway(item)">上架</button>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import toast from '@/utils/toast'
import { getWareInPutawayDetail } from '@/api/wareInPutaway'

export default {
  name: 'WareInPutawayDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const loading = ref(false)
    const errorMsg = ref('')
    const detail = ref(null)

    const handleBack = () => router.back()

    const parseFloatSafe = (val) => {
      const n = Number(val)
      return Number.isNaN(n) ? 0 : n
    }

    const normalizeInt = (n) => {
      const rounded = Math.round(n)
      return Math.abs(n - rounded) < 1e-6 ? rounded : n
    }

    const qtyText = (wareInNum, packageNum, unitName) => {
      const total = normalizeInt(parseFloatSafe(wareInNum))
      const perCarton = normalizeInt(parseFloatSafe(packageNum))
      const unit = unitName || '个'

      if (!perCarton) {
        return `${total}${unit}`
      }

      const carton = Math.floor(total / perCarton)
      const piece = normalizeInt(total - carton * perCarton)

      return `${carton}箱${piece}${unit}`
    }

    const palletItems = computed(() => {
      const list = detail.value?.wareInDetailList || []
      return list.map((d) => {
        const vo = Array.isArray(d?.wareInDetailRecordVOList) ? d.wareInDetailRecordVOList[0] : null
        return {
          id: d?.id,
          materialNo: d?.materialNo,
          materialDesc: d?.materialDesc,
          batchNo: d?.batchNo,
          palletCode: vo?.palletCode,
          palletId: vo?.palletId,
          qtyText: qtyText(d?.wareInNum, d?.packageNum, d?.packageUnitName)
        }
      })
    })

    const wareInTypeText = (type) => {
      if (type === 0 || type === '0') return '收货入库'
      if (type === 1 || type === '1') return '退货入库'
      if (type === 4 || type === '4') return '计划取消'
      return '收货入库'
    }

    const fetchDetail = async () => {
      const id = route.params.id || route.query.id
      if (!id) {
        errorMsg.value = '缺少入库单ID'
        return
      }

      loading.value = true
      errorMsg.value = ''
      try {
        const res = await getWareInPutawayDetail(id)
        detail.value = res?.data || null
      } catch (e) {
        errorMsg.value = e?.message || '加载失败'
      } finally {
        loading.value = false
      }
    }

    const handlePutaway = (item) => {
      const palletCode = item?.palletCode
      const palletId = item?.palletId
      const wareInId = detail.value?.id
      const storeId = detail.value?.storeId
      const supplierId = detail.value?.supplierId

      if (!palletCode) {
        toast.error('缺少托盘码')
        return
      }
      if (!palletId) {
        toast.error('缺少托盘ID')
        return
      }
      if (!wareInId) {
        toast.error('缺少入库单ID')
        return
      }
      if (!storeId || !supplierId) {
        toast.error('缺少仓库或供应商信息')
        return
      }

      const basePath = router.currentRoute.value.path.startsWith('/mobile') ? '/mobile/warein-putaway' : '/warein-putaway'
      router.push({
        path: `${basePath}/scan`,
        query: {
          wareInId,
          palletCode,
          palletId,
          storeId,
          supplierId
        }
      })
    }

    onMounted(() => {
      fetchDetail()
    })

    return {
      loading,
      errorMsg,
      detail,
      palletItems,
      wareInTypeText,
      handleBack,
      handlePutaway
    }
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 10px 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
}

.loading,
.error {
  padding: 16px;
  background: var(--card-background);
  border-radius: 12px;
  color: var(--text-secondary);
}

.detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section {
  background: var(--card-background);
  border-radius: 12px;
  overflow: hidden;
}

.section-title {
  padding: 10px 4px;
  font-size: 14px;
  color: var(--text-secondary);
}

.section-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
}

.section-row.bold .value {
  font-weight: 600;
}

.label {
  font-size: 13px;
  color: var(--text-secondary);
}

.value {
  font-size: 13px;
  color: var(--text-primary);
  text-align: right;
  max-width: 70%;
  word-break: break-all;
}

.divider {
  height: 1px;
  background: var(--border-color);
}

.action-row {
  padding: 12px 14px;
  display: flex;
  justify-content: flex-end;
}

.primary-btn {
  min-width: 96px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: var(--primary-color);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
}
</style>
